const Camur = require("@/utils/camurutils").default;
const Enum = require('@/utils/enums').default;
const RecordingCache = require("@/recordings/recordingcache").default;
import * as RecordingBlocks from "@/recordings/recordingblocks";
import { markRaw, toRaw } from "vue";

const CACHE_INVALIDATION_DAYS = 30;
const CACHE_SETTLE_MS = 3 * 24 * 60 * 60 * 1000;
const MAX_RESOLUTION = 500;
const BACK_BUFFER_TIMEOUT_MS = 10000;
const WIDER_PLOT_FACTOR = 1.25;

export default class GraphBuffer {
  constructor(project, graph_data_api) {
    this.state = 0;
    this.project = project;
    this.graph_data_api = graph_data_api;
    this.from_date = null;
    this.to_date = null;
    this.series = [];
    this.hidden_series = [];
    this.zone_ids = [];
    this.recording_cache = new RecordingCache(
      project.dto.id,
      CACHE_INVALIDATION_DAYS
    );
    this.resolution = "?";
    this.max_resolution = MAX_RESOLUTION;
    this.fetched_blocks = [];
    this.expected_blocks = new Set();
    this.fetch_batch_size = 0;
    this.fetched_blocks_in_batch = 0;
    this.report_type = 0;
    this.on_all_data_loaded_callback = null;
    this._render_raf = null;
    this._recordings = [];
    this._rec_ranges = [];
    this._rec_by_id = new Map();
    this._sample_intervals_ms = new Map();
    this._value_tag = null;
    this._bucket_s = null;
    this._bucket_plot_width = 0;
    this._back_series = null;
    this._back_gen = 0;
    this._back_timer = null;
    this._color_channel_ids = [];
    this._hold_render = true;
    this._hold_timer = null;
  }

  // Value tag for the depolarization result plotted in the decay-results
  // "all recordings" view, taken from the zone setting
  // depolarizationCriteriaCompareValue:
  //   0 (or missing) -> max value  -> tag 1002
  //   1              -> last value -> tag 1001
  depolarization_value_tag(zone_ids) {
    const MAX_VALUE_TAG = 1002;
    const LAST_VALUE_TAG = 1001;
    if (!zone_ids || zone_ids.length === 0) return MAX_VALUE_TAG;
    let tag = null;
    for (let zone_id of zone_ids) {
      let zone = this.project.zones.find(z => z.id == zone_id);
      let compare_value = zone?.dto?.depolarizationCriteriaCompareValue;
      let zone_tag = (compare_value == 1) ? LAST_VALUE_TAG : MAX_VALUE_TAG;
      if (tag == null) tag = zone_tag;
      else if (tag != zone_tag) return MAX_VALUE_TAG;
    }
    return tag ?? MAX_VALUE_TAG;
  }

  get recordings() { return this._recordings; }
  set recordings(val) {
    this._recordings = val;
    let ranges = val.map(r => ({
      from: new Date(r.start).getTime(),
      to: r.stop != null ? new Date(r.stop).getTime() : Infinity,
      id: r.id
    }));
    let sample_intervals = new Map();
    for (const r of val) {
      const sec = this._sample_interval_seconds(r);
      if (sec && sec > 0) sample_intervals.set(r.id, sec * 1000);
    }
    this._rec_ranges = markRaw(ranges);
    this._rec_by_id = markRaw(new Map(ranges.map(r => [r.id, r])));
    this._sample_intervals_ms = markRaw(sample_intervals);
    this._reconcile_all();
  }

  _sample_interval_seconds(r) {
    if (!r || !r.dto) return null;
    const type = r.dto.type;
    const rt = Camur.recording_type;
    if (type === rt.rectype_decay) return r.dto.decay?.sampleInterval ?? null;
    if (type === rt.rectype_monitor) return r.dto.monitor?.sampleInterval ?? null;
    if (type === rt.rectype_zra) return r.dto.zra?.sampleInterval ?? null;
    if (type === rt.rectype_resmes) return r.dto.resMes?.sampleInterval ?? null;
    if (type === rt.rectype_lpr) {
      const on = r.dto.lpr?.timeOn, off = r.dto.lpr?.timeOff;
      return (on != null && off != null) ? on + off : null;
    }
    return null;
  }

  is_empty() {
    return this.series.length === 0;
  }

  update(
    from_date,
    to_date,
    display_from,
    display_to,
    recording_id,
    report_type,
    recording_type,
    zone_ids,
    enable_caching = true,
    plot_width = null
  ) {

    if (recording_type == 0) {
      // No specific recording id selected
      if (report_type === Camur.report_type.reptype_monitor) {
        recording_type = Camur.recording_type.rectype_monitor;
      } else if (report_type === Camur.report_type.reptype_decay) {
        recording_type = Camur.recording_type.rectype_decay;
      } else if (report_type === Camur.report_type.reptype_lpr_measurements) {
        recording_type = Camur.recording_type.rectype_lpr;
      } else if (report_type === Camur.report_type.reptype_lpr_results) {
        recording_type = Camur.recording_type.rectype_lpr_results;
      }  else if (report_type === Camur.report_type.reptype_zra) {
        recording_type = Camur.recording_type.rectype_zra;
      }  else if (report_type === Camur.report_type.reptype_resmes) {
        recording_type = Camur.recording_type.rectype_resmes;
      }  else if (report_type === Camur.report_type.reptype_decay_results) {
        recording_type = Camur.recording_type.rectype_decay_results;
        display_from = from_date;
        display_to = to_date;   
      }
    }

    this.from_date = from_date;
    this.to_date = to_date;
    this.zone_ids = zone_ids;

    this.report_type = report_type;

    let filters = {
      recordingId: recording_id,
      recordingType: recording_type
    };

    let adaptive = plot_width > 0 &&
      RecordingBlocks.adaptive_resolution_enabled() &&
      report_type !== Camur.report_type.reptype_decay_results &&
      recording_type != Camur.recording_type.rectype_decay_results;

    let current_bucket_s = this._bucket_s;
    if (adaptive && current_bucket_s && plot_width > this._bucket_plot_width * WIDER_PLOT_FACTOR) {
      current_bucket_s = null;
    }
    let resolution = adaptive
      ? RecordingBlocks.get_adaptive_resolution(display_from, display_to, plot_width, current_bucket_s)
      : RecordingBlocks.get_resolution_for_timespan(display_from, display_to);
    if (adaptive && (current_bucket_s == null || resolution.bucket_s !== this._bucket_s)) {
      this._bucket_plot_width = plot_width;
    }

    if (resolution.name != this.resolution) {
      this.resolution = resolution.name;
      this._bucket_s = adaptive ? resolution.bucket_s : null;
      if (adaptive && !this.is_empty()) this._start_back_buffer();
      else this.reset();
    }

    let all_flag = false;
    let value_tag = 0;
    if (recording_type == Camur.recording_type.rectype_decay_results) {
      all_flag = true;
      if (recording_id ==-1) {
        value_tag = this.depolarization_value_tag(zone_ids);
        if (this._value_tag != null && value_tag != this._value_tag) {
          this._value_tag = value_tag;
          this.reset();
        } else {
          this._value_tag = value_tag;
        }
      }
    }

    let compute_required_blocks = () => {
      if (!enable_caching) {
        // If we are not caching, just load all data as one big request
        return [{ from: from_date, to: to_date, max_values: this.max_resolution }];
      }
      let blocks = adaptive
        ? RecordingBlocks.get_adaptive_blocks_for_timespan(from_date, to_date, resolution)
        : RecordingBlocks.get_recording_blocks_for_timespan(
          from_date,
          to_date,
          display_from,
          display_to
        );
      // Ignore any block that we already have fetched
      blocks = blocks.filter(b => !this.fetched_blocks.includes(b.hash));
      // Ignore any blocks from the future!
      let now = new Date(Date.now());
      blocks = blocks.filter(b => b.from <= now);
      return blocks;
    };

    let required_blocks = compute_required_blocks();

    if (required_blocks.length > 0 && this.is_to_big()) {
      if (adaptive && !this.is_empty()) this._start_back_buffer();
      else this.reset();
      required_blocks = compute_required_blocks();
    }

    let cached_block_count = 0;
    for (let block of required_blocks) {
      this.fetched_blocks.push(block.hash);
      this.load_block(block, filters, zone_ids, enable_caching, all_flag, value_tag,
        () => { cached_block_count++; });
    }
    const holding = this._hold_render && this.expected_blocks.size > 0;
    if (this._back_series && this.expected_blocks.size === 0) {
      this._show_back_buffer();
    } else if (cached_block_count > 0 && !this._back_series && !holding) {
      // If any blocks were served from cache (synchronous), trigger a single
      // render now instead of one per channel per block.
      // Cached blocks were merged raw (deferred); reconcile the whole buffer once
      // before the single batched render.
      this._reconcile_all();
      this.assign_colors_to_series();
      this.state++;
    }
    if (this._hold_render) {
      if (this.expected_blocks.size === 0) this._release_render_hold();
      else this._arm_render_hold_timeout();
    }

    // If ALL required blocks came from cache, expected_blocks is never populated
    // so on_expected_block_loaded never fires → fire the callback here instead.
    if (cached_block_count > 0 && cached_block_count === required_blocks.length && this.on_all_data_loaded_callback) {
      setTimeout(() => {
        if (this.on_all_data_loaded_callback) {
          this.on_all_data_loaded_callback();
        }
      }, 0);
    }

    // If no blocks were needed (all cached or no data), fire callback immediately
    if (required_blocks.length === 0 && this.on_all_data_loaded_callback) {
      // Use setTimeout to make it async like the network callbacks
      setTimeout(() => {
        if (this.on_all_data_loaded_callback) {
          this.on_all_data_loaded_callback();
        }
      }, 0);
    }
  }

  load_block(block_info, filters, zone_ids, enable_caching, all_flag, value_tag, on_cached = null) {
    let channel_ids = [];
    for (let zone of this.project.zones.filter(z => zone_ids.includes(z.id))) {
       for  (let node of zone.nodes) {
        if (this.selected_node(zone, node)) {
          for (let channelkey in node.channels) {
            let channel = node.channels[channelkey];
            if (channel.userVisible) {
              if (channel.lastValueUpdated > "2020-01-01") {
                if (this.relevantChannel(channel, filters.recordingType))
                  channel_ids.push(channel.id);
              }  
            }
          }
        }
      }
    }

    if (channel_ids.length == 0)
        // Do not request if no channels
        // Otherwise values for all channels will be fetched
      return;
    this._color_channel_ids = channel_ids;

    let cache_key = {
      f: block_info.from,
      t: block_info.to,
      z: zone_ids,
      c: [...channel_ids].sort((a, b) => a - b),
      i: [filters.recordingId],
      type: [filters.recordingType],
      r: block_info.max_values
    };
    let cacheable = enable_caching && !all_flag;

    // If this block is cached, use that instead!
    if (cacheable && this.recording_cache.has_data_for_dates(cache_key)) {
      let cached_block = this.recording_cache.load(cache_key);
      if (cached_block) {
        let converted_block = this.convert_block_for_graph(cached_block, null);
        this.add_block_to_buffer(converted_block, true, true); // silent + defer reconcile: update() reconciles + renders once after the loop
        if (on_cached) on_cached();
        return;
      }
    }

    let enable_cache_flag = cacheable && block_info.to < Date.now() - CACHE_SETTLE_MS;
    this.expected_blocks.add(block_info.hash);
 
    this.fetch_batch_size++;
    this.graph_data_api.fetch(
      block_info.from,
      block_info.to,
      block_info.max_values,
      this.project.id,
      cache_key,
      channel_ids,
      filters,
      enable_cache_flag,
      all_flag,
      value_tag,
      () => {
        this.on_expected_block_loaded(block_info);
      }
    );
  }

  selected_node(zone, node) {
    if (window.appm.store.sltd.zone_is_selected()) {
      if (zone.id == window.appm.store.sltd.zone.id)
        return true;
      else
        return false;
    } else {
      return node.app_prop.include_select;
    }
  }

  on_expected_block_loaded(block_info) {
    if (!this.expected_blocks.delete(block_info.hash)) return;
    this.fetched_blocks_in_batch++;
    if(this.expected_blocks.size === 0) {
      this.fetch_batch_size = 0;
      this.fetched_blocks_in_batch = 0;
      this._cancel_scheduled_render();
      this._release_render_hold();
      if (this._back_series) this._swap_back_buffer();
      this._reconcile_all();
      this.assign_colors_to_series();
      this.state++;

      // Fire callback when all data has been loaded
      if (this.on_all_data_loaded_callback) {
        this.on_all_data_loaded_callback();
      }
    }
  }

  add_downloaded_block(block, metadata) {
    if (metadata != null && metadata.cache_data) {
      this.cache_block(block, metadata);
    }
    if (this._is_stale_block(metadata)) return;
    let converted_block = this.convert_block_for_graph(block, metadata);
    this.add_block_to_buffer(converted_block, true, true);
    if (!this._back_series && !this._hold_render) this._schedule_render();
  }

  _release_render_hold() {
    this._hold_render = false;
    clearTimeout(this._hold_timer);
    this._hold_timer = null;
  }

  _arm_render_hold_timeout() {
    if (this._hold_timer != null) return;
    this._hold_timer = setTimeout(() => {
      this._hold_timer = null;
      if (!this._hold_render) return;
      this._hold_render = false;
      this._schedule_render();
    }, BACK_BUFFER_TIMEOUT_MS);
  }

  _is_stale_block(metadata) {
    if (this._bucket_s == null || metadata == null) return false;
    let hash = RecordingBlocks.hash_from_block({
      from: metadata.from,
      to: metadata.to,
      max_values: metadata.max_values
    });
    return !this.expected_blocks.has(hash);
  }

  convert_block_for_graph(block, metadata) {
    // Reformat the buffer to work with the graph
    let color_index = 0;
    let color_scheme = Camur.get_series_color_scheme();
    let resulting_series = [];

    //console.log(JSON.stringify(metadata));
    // console.log("block series", block.series)
    // console.log("channel series", this.series)
    for (let series of block.series) {
      let channel = this.project.find_channel(series.channelId);
      if (channel === null) continue;
      if (channel.io === Camur.io_type.io_type_output) continue; // Ignore outputs here
      let node = this.project.find_node(channel.nodeId);

      let graph_series = {};
      graph_series.name = node.name + "-" + channel.name;
      graph_series.id = series.channelId;
      graph_series.unit = channel.formula.unit;
      graph_series.decimals = channel.formula.decimals;
      if (metadata == null) {
        graph_series.graph_min = channel.formula.graphScaleMin;
      } else {
        graph_series.graph_min = (metadata.filters.recordingType == Camur.recording_type.rectype_decay_results) ? 0 : channel.formula.graphScaleMin;
      }
      graph_series.graph_max = channel.formula.graphScaleMax;
      graph_series.color = color_scheme[color_index % color_scheme.length];
      // this.assign_colors_to_series();
      graph_series.override_y_axis_scale = false;
      graph_series.override_y_axis_scale_min = 0;
      graph_series.override_y_axis_scale_max = 200;

      graph_series.grid = false;
      graph_series.zero_line = false;
      graph_series.draw_line_at = false;
      graph_series.draw_line_at_value = 100;
      graph_series.yLabel = "";
      graph_series.yPos = Enum.graph_pos.graph_pos_bottom;
      graph_series.graph_type = Enum.graph_type.graph_type_line;
      graph_series.settings_updated = false;
      color_index++;
  
      graph_series.data = series.data.map(sample => {
        return {
          minDate: new Date(sample[0]),
          maxDate: new Date(sample[1]),
          minVal: sample[2],
          maxVal: sample[3],
          avgVal: sample[5] ?? null,
          // 7th element is recording_id (server API_1.0.0.27+); null markers
          // omit it. Keeping it on the data point lets hover show which
          // recording the hovered measurement belongs to.
          recordingId: sample[6] ?? null
        };
      });
      resulting_series.push(graph_series);
      //console.log("Push serie:"+JSON.stringify(graph_series.data));
    }
    // color_index = 0
    // console.log("this.series", this.series)
    // for (let serie of this.series) {
    //   let channel = this.project.find_channel(serie.id);
    //   let node = this.project.find_node(channel.nodeId);
    //   if (node.is_power_supply && (channel.code == "U" || channel.code == "I")) {
    //     if (channel.code == "U") {
    //       serie.color = "red"
    //     }
    //     if (channel.code == "I") {
    //       serie.color = "blue"
    //     }
    //   } else {
    //     serie.color = color_scheme[color_index % color_scheme.length];
    //     color_index ++;
    //   }
    // }

    return {
      ...block,
      series: resulting_series
    };
  }

  assign_colors_to_series() {
    const color_scheme = Camur.get_series_color_scheme();
    const keys = new Map();
    const key_of = id => {
      if (!keys.has(id)) {
        const channel = this.project.find_channel(id);
        const node = channel ? this.project.find_node(channel.nodeId) : null;
        keys.set(id, `${node?.name}-${channel?.name}`);
      }
      return keys.get(id);
    };
    const by_name = (a, b) => key_of(a).localeCompare(key_of(b));

    this.series.sort((a, b) => by_name(a.id, b.id));

    const ids = [...new Set([...this._color_channel_ids, ...this.series.map(s => s.id)])];
    ids.sort(by_name);

    const colors = new Map();
    let color_index = 0;
    let has_voltage = false;
    let has_current = false;
    for (const id of ids) {
      const channel = this.project.find_channel(id);
      if (!channel || channel.io === Camur.io_type.io_type_output) continue;
      const node = this.project.find_node(channel.nodeId);
      if (node?.is_power_supply && channel.code == "U" && !has_voltage) {
        colors.set(id, "red");
        has_voltage = true;
      } else if (node?.is_power_supply && channel.code == "I" && !has_current) {
        colors.set(id, "blue");
        has_current = true;
      } else {
        colors.set(id, color_scheme[color_index % color_scheme.length]);
        color_index++;
      }
    }

    for (const serie of this.series) {
      if (colors.has(serie.id)) serie.color = colors.get(serie.id);
    }
  }

  // Stores a downloaded block under the key load_block() built for its request.
  cache_block(block, metadata) {
    if (metadata != null && metadata.cache_data && metadata.cache_key) {
      this.recording_cache.store(metadata.cache_key, block);
    }
  }

  is_to_big() {
    return this.fetched_blocks.length >= 50;
  }

  is_waiting_for_blocks() {
    return this.expected_blocks.size > 0;
  }

  reset() {
    this._cancel_scheduled_render();
    this._back_series = null;
    clearTimeout(this._back_timer);
    this._release_render_hold();
    this._hold_render = true;
    this.state += 1;
    this.series = [];
    this.fetched_blocks = [];
    this.expected_blocks.clear();
    this.fetch_batch_size = 0;
    this.fetched_blocks_in_batch = 0;
  }

  _start_back_buffer() {
    this._cancel_scheduled_render();
    this._back_series = [];
    this.fetched_blocks = [];
    this.expected_blocks.clear();
    this.fetch_batch_size = 0;
    this.fetched_blocks_in_batch = 0;
    let gen = ++this._back_gen;
    clearTimeout(this._back_timer);
    this._back_timer = setTimeout(() => {
      if (gen !== this._back_gen || !this._back_series) return;
      this._show_back_buffer();
    }, BACK_BUFFER_TIMEOUT_MS);
  }

  _swap_back_buffer() {
    clearTimeout(this._back_timer);
    let shown = new Map(this.series.map(s => [s.id, s]));
    this.series = this._back_series.map(s => {
      let old = shown.get(s.id);
      if (!old) return s;
      old.data = s.data;
      return old;
    });
    this._back_series = null;
  }

  _show_back_buffer() {
    this._swap_back_buffer();
    this._reconcile_all();
    this.assign_colors_to_series();
    this.state++;
  }

  _schedule_render() {
    if (this._render_raf != null) return;
    const raf = (typeof requestAnimationFrame === "function")
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 16);
    this._render_raf = raf(() => {
      this._render_raf = null;
      this._reconcile_all();
      this.assign_colors_to_series();
      this.state++;
    });
  }

  _cancel_scheduled_render() {
    if (this._render_raf == null) return;
    if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(this._render_raf);
    else clearTimeout(this._render_raf);
    this._render_raf = null;
  }

  _strip_nulls_from_data(data) {
    if (!this._rec_ranges.length) return data;
    return data.filter(d => {
      if (d == null) return false;
      if (d.minVal != null || d.maxVal != null) return true;
      const t = (d.minDate ?? d.date)?.getTime();
      return t == null || !this._rec_ranges.some(r => t >= r.from && t < r.to);
    });
  }

  _recordings_touch(prevId, nextId) {
    if (prevId == null || nextId == null) return false;
    if (prevId == nextId) return false;
    const prev = this._rec_by_id.get(prevId);
    const next = this._rec_by_id.get(nextId);
    if (!prev || !next) return false;
    if (!isFinite(prev.to)) return false;
    const MIN_MS = 60 * 1000;
    return Math.floor(prev.to / MIN_MS) === Math.floor(next.from / MIN_MS);
  }

  _reconcile_all() {
    for (const s of this.series) {
      if (this.report_type === Camur.report_type.reptype_decay_results) {
        s.data = this._strip_nulls_from_data(s.data);
      } else {
        this._reconcile_nulls(s);
      }
    }
  }

  // Decide each server-emitted null marker by the recording identity of its
  // nearest real samples, instead of by recording time-ranges. A null whose
  // neighbours on both sides belong to the SAME recording is normally a
  // sparse-sampling / block-boundary artifact (the bucket width is finer
  // than the sample interval) and is removed so the line stays continuous —
  // EXCEPT when the null's duration is clearly longer than the recording's
  // configured sample interval (see _is_real_within_recording_gap), in which
  // case it represents missed sample(s) inside the recording and is kept.
  // A null between two DIFFERENT recordings is a real boundary gap and is
  // kept — UNLESS the prev recording's stop time equals the next recording's
  // start time, in which case the two recordings are back-to-back on the
  // timeline and the line should connect (the null is stripped). This is local to the data — it
  // needs neither the recordings list nor any assumption about block arrival
  // order, so it is stable under incremental loading.
  //
  // Edge handling:
  //   - leading null (no real sample before)  → strip (a break before any data
  //     is meaningless).
  //   - trailing null (no real sample after)  → keep (it is the cross-block
  //     stitch signal; once the next block merges it gets a real right
  //     neighbour and is re-judged; at the true end of data it draws nothing).
  //   - a neighbour whose recordingId is unknown (legacy data cached before the
  //     id existed) → fall back to the range-based rule for that null.
  //
  // It ALSO inserts a break between two ADJACENT real samples that belong to
  // different recordings and have a time gap between them but NO null in
  // between. The server only emits an empty-bucket null when a gap spans a
  // whole bucket; when zoomed out the recording-boundary gap can be smaller
  // than one bucket, so the two recordings' clamped entries sit next to each
  // other and the line would bridge the boundary. We detect the recording
  // change plus the time gap (prev.maxDate < next.minDate) and synthesise the
  // missing break, so real between-recording gaps show at every zoom level.
  _reconcile_nulls(s) {
    const data = toRaw(s.data);
    const n = data.length;
    if (n === 0) return;

    const NO_REAL = -1; // sentinel: no real sample on that side
    const isReal = d => d != null && (d.minVal != null || d.maxVal != null);
    const idOf = d => (d.recordingId != null ? d.recordingId : null);
    const startOf = d => d?.minDate?.getTime?.() ?? null;
    const endOf = d => (d?.maxDate ?? d?.minDate)?.getTime?.() ?? null;

    // For each index, the recordingId AND minDate of the next real sample
    // strictly after the index (or NO_REAL / null). The real-neighbour TIMES
    // are what determine whether a null is a real gap — the null marker's own
    // duration can be tiny (server emits a trailing null per block, so a
    // multi-block gap is represented by several small nulls rather than one
    // big one).
    const nextId = new Array(n);
    const nextStart = new Array(n);
    let seenId = NO_REAL;
    let seenStart = null;
    for (let i = n - 1; i >= 0; i--) {
      nextId[i] = seenId;
      nextStart[i] = seenStart;
      if (isReal(data[i])) {
        seenId = idOf(data[i]);
        seenStart = startOf(data[i]);
      }
    }

    const out = [];
    let prevId = NO_REAL;
    let prevEnd = null;
    for (let i = 0; i < n; i++) {
      const d = data[i];
      if (d == null) continue;
      if (isReal(d)) {
        // Synthesise a break when adjacent real samples have a time gap and no
        // null between them: (a) different recordings that aren't back-to-back
        // (sub-bucket recording boundary the server misses), OR (b) same
        // recording with a gap longer than 1.5× the configured sample interval
        // (the server's nulls can be missing or too small to survive on their
        // own — see real-gap rule below).
        const last = out.length ? out[out.length - 1] : null;
        if (last && isReal(last)) {
          const a = idOf(last), b = idOf(d);
          const aStop = endOf(last);
          const bStart = startOf(d);
          if (a != null && b != null &&
              aStop != null && bStart != null && aStop < bStart) {
            let needBreak = false;
            if (a != b) {
              needBreak = !this._recordings_touch(a, b);
            } else {
              const intervalMs = this._sample_intervals_ms?.get(a);
              if (intervalMs && (bStart - aStop) > 1.5 * intervalMs) needBreak = true;
            }
            if (needBreak) {
              out.push({ minDate: last.maxDate ?? last.minDate, maxDate: d.minDate, minVal: null, maxVal: null });
            }
          }
        }
        out.push(d);
        prevId = idOf(d);
        prevEnd = endOf(d);
        continue;
      }
      // d is a null marker — decide keep/strip.
      const after = nextId[i];
      const afterStart = nextStart[i];
      if (prevId === NO_REAL) continue;        // leading null → strip
      if (after === NO_REAL) { out.push(d); continue; } // trailing → keep
      if (prevId !== null && after !== null) {
        if (prevId == after) {
          // Same recording on both sides. Default: sparse-sampling / block-
          // edge artifact → strip. EXCEPTION: if the time between the prev
          // real sample's END and the next real sample's START is clearly
          // longer than the recording's configured sample interval, it
          // represents missed sample(s) inside the recording and is shown as
          // a real gap. We measure the REAL-NEIGHBOUR gap (not the null's
          // own duration) because the server's trailing-null-per-block scheme
          // can fragment one large gap into several small null markers.
          const intervalMs = this._sample_intervals_ms?.get(prevId);
          if (intervalMs && prevEnd != null && afterStart != null &&
              (afterStart - prevEnd) > 1.5 * intervalMs) {
            out.push(d);
            continue;
          }
          continue;
        }
        if (this._recordings_touch(prevId, after)) continue; // back-to-back recordings → strip
        out.push(d);                           // different recordings, real gap → keep
        continue;
      }
      // Fallback: a neighbour's id is unknown (legacy). Use the range rule.
      const t = (d.minDate ?? d.date)?.getTime();
      if (t != null && this._rec_ranges.some(r => t >= r.from && t < r.to)) continue;
      out.push(d);
    }
    s.data = out;
  }

  // defer_reconcile: skip the per-series null reconciliation here so a batch of
  // blocks can be merged raw and reconciled ONCE when the batch completes (see
  // on_expected_block_loaded for network blocks, update() for cached blocks).
  // Per-block reconcile is O(n) over the whole series each time → O(n²) across a
  // load, and reconciling the fully-merged raw buffer in one pass is also more
  // correct: every null marker can see its real neighbours across all blocks.
  add_block_to_buffer(block, silent = false, defer_reconcile = false) {
    let target = this._back_series ?? this.series;
    for (let block_series of block.series) {
      // Ignore empty series
      // No, keep and show empty instead, 2024-04-23
      //  if (block_series.data.length === 0) continue;

      // Merge the raw block (null markers included). Null cleanup runs on the
      // FULL buffer series afterwards via _reconcile_nulls so it can see each
      // null's real neighbours across block boundaries — a per-block strip
      // can't, which is what produced the transient block-edge gaps.

      // Add any channels that does not yet exist on the buffer
      let channel_exist = target.some(s => s.id === block_series.id);
      if (!channel_exist) {
        target.push({
          ...block_series,
          data: []
        });
      }

      // Find the same matching series in the buffer and add the block to that
      let buffer_series = target.find(s => s.id === block_series.id);
      this.add_block_series_to_buffer_series(block_series, buffer_series);
      if (this.report_type === Camur.report_type.reptype_decay_results) {
        // Decay results are bar charts with no line-gap semantics — keep the
        // legacy range-based strip.
        if (!defer_reconcile) buffer_series.data = this._strip_nulls_from_data(buffer_series.data);
      } else {
        if (!defer_reconcile) this._reconcile_nulls(buffer_series);
      }
    }
    // Increment once per block (not once per series) so the graph re-renders
    // once per arriving block rather than once per channel in the block.
    // Pass silent=true when the caller will trigger a render itself after
    // all blocks are merged (e.g. on_expected_block_loaded).
    if (!silent) this.state++;
  }

  add_block_series_to_buffer_series(block_series, series) {
    if (block_series.data.length == 0) {
      // Nothing to add
      return;
    }

    // If empty, just copy
    if (series.data.length == 0) {
      series.data = block_series.data;
      return;
    }

    let first_date_of_block = block_series.data[0].minDate;
    let last_date_of_block =
      block_series.data[block_series.data.length - 1].minDate;
    let first_date_of_buffer = series.data[0].minDate;
    let last_date_of_buffer = series.data[series.data.length - 1].minDate;

    if (last_date_of_block <= first_date_of_buffer) {
      // Add to the beginning of the array
      this.insert_to_array(series.data, block_series.data, 0);
    } else if (first_date_of_block >= last_date_of_buffer) {
      // Add to the end of the array
      this.insert_to_array(series.data, block_series.data, series.data.length);
    } else {
      // Replace old data covered by the new block's time range to keep the array
      // chronologically sorted and avoid duplicate data at different resolutions.
      let start_remove = series.data.findIndex(d => d && d.minDate >= first_date_of_block);
      if (start_remove === -1) start_remove = series.data.length;
      let end_remove = series.data.findIndex(d => d && d.minDate >= last_date_of_block);
      if (end_remove === -1) end_remove = series.data.length;
      series.data.splice(start_remove, end_remove - start_remove, ...block_series.data);
    }
  }

  relevantChannel(channel, recordingType) 
  {
    if (recordingType > 0) {
      switch (recordingType) {
        case Camur.recording_type.rectype_lpr_results:
          let node = this.project.find_node(channel.nodeId);
          // Only include iCorr and iSigma
          if ((node.capability == Camur.node_type.ntype_corrowatch) || (node.capability == Camur.node_type.ntype_ladder)) {
            if (channel.no >= 24)
              return true;
            else
              return false;  
          } else if (node.capability == Camur.node_type.ntype_lpr) {
            if (channel.no >= 3)
              return true;
            else
              return false;  
          } else {
            return false;  
          }
        default:
          return true;
      }
    }
    else
      return true;
  }

  insert_to_array(array, elements, index) {
    let el_length = elements.length;
    let arr_length = el_length + array.length;
    array.length = arr_length;
    let limit = arr_length - index;

    for (let i = 1; i <= limit; i++) {
      let index_to_copy_from = arr_length - i;
      array[index_to_copy_from] = array[index_to_copy_from - el_length];
    }

    for (let i = 0; i < el_length; i++) {
      array[index + i] = elements[i];
    }
  }
}
